/**
 * adVisibility.js
 *
 * Reklame-synlighed styret af ABONNEMENTSTYPE (ikke role).
 *
 * Design (per user 01-Jul-2026):
 *
 *   Subscription    | Bundreklamer | In-feed sponsor-rækker
 *   ----------------+--------------+------------------------
 *   Free            |      ✅       |          ❌
 *   Light           |      ❌       |          ✅
 *   Pro             |      ❌       |          ❌
 *
 * Employees (role='editor'/'admin') ser ALDRIG reklamer, uanset
 * subscription_type. De håndteres separat via `isEmployee()`.
 *
 * Reklame-komponenter må ALDRIG hardcode `role === 'guest'` eller
 * `role === 'pro'` — brug altid helpers herfra. Når Light senere
 * aktiveres via Stripe skal INTET af dette ændres.
 */
import {
  SUBSCRIPTION_TYPES,
  getSubscriptionType,
  isEmployee,
} from '../config/subscriptions';

// Hvilke abonnementer ser hvad. Nemt at udvide (fx tilføj LIGHT_PLUS senere).
const BOTTOM_BANNER_SUBSCRIPTIONS = new Set([SUBSCRIPTION_TYPES.FREE]);
const IN_FEED_SPONSOR_SUBSCRIPTIONS = new Set([SUBSCRIPTION_TYPES.LIGHT]);

/**
 * Skal denne bruger se de traditionelle bund-reklamer?
 * Gælder for Free-abonnement (både anonyme gæster og registrerede
 * gratis-brugere). Skjules for employees.
 */
export function showsBottomBanners(user) {
  if (isEmployee(user)) return false;
  return BOTTOM_BANNER_SUBSCRIPTIONS.has(getSubscriptionType(user));
}

/**
 * Skal denne bruger se fuld-bredde in-feed sponsor-rækker mellem
 * opskrifterne? Gælder pt. kun for Light-abonnement. Skjules for
 * employees.
 */
export function showsInFeedSponsorRows(user) {
  if (isEmployee(user)) return false;
  return IN_FEED_SPONSOR_SUBSCRIPTIONS.has(getSubscriptionType(user));
}

/**
 * Ser brugeren nogen form for reklame? Bekvem OR-funktion.
 */
export function showsAnyAds(user) {
  return showsBottomBanners(user) || showsInFeedSponsorRows(user);
}

/**
 * Debug helper: hvorfor ser brugeren de(n)/den(de) reklamer?
 */
export function explainAdVisibility(user) {
  const subscription = getSubscriptionType(user);
  const employee = isEmployee(user);
  return {
    subscription_type: subscription,
    is_employee: employee,
    bottomBanners: !employee && BOTTOM_BANNER_SUBSCRIPTIONS.has(subscription),
    inFeedSponsorRows: !employee && IN_FEED_SPONSOR_SUBSCRIPTIONS.has(subscription),
  };
}
